import JsonQuery from 'json-query';
import { localization, utils as commonUtils } from '@sitebuilder/common';
import { AnnotationFilters } from '../Annotations/Annotations';

export interface Config {
    iconShowContent?: string;
    iconHideContent?: string;
    useIconForFavoriteColumn?: string;
    addPlaceholderAfterFilters?: boolean;
    isShowClearSelectionButton?: boolean;
    clearSelectionButtonName?: string;
    emailBtnTransformFunc?: string;
    isShowEmailButton?: boolean;
    emailBtnDataKey?: string;
    receiverEmailId?: string;
    emailSubject?: string;
    emailOnHoverText?: string;
    emailButtonText?: string;
    emailBtnLocalStorageItemName: string;
    copyBtnTransformFunc?: string;
    isShowCopyButton?: boolean;
    copyBtnDataKey?: string;
    copyOnHoverText?: string;
    copyButtonText?: string;
    copyBtnLocalStorageItemName: string;
    firstColumnOptions: FirstColumnOption;
    hideTabHeader: boolean;
    selectedListStorageName: string;
    selectedListDataId: string;
    maxSelectionList: number;
    selectedListJsonPath: string;
    views: {
        id: string;
        label: string;
        subHeading: string;
        columns?: {
            id: string;
            label: string;
            jsonPath: string;
            format?: string;
            nullGuard?: string;
            noDataAnnotationId?: string;
            annotationId?: string;
            additionalId?: string;
            additionalJsonPath?: string;
            additionalFormat?: string;
            disableSorting: boolean;
            transformFunction?: (input: any) => any;
            columnType?: 'risk' | 'price' | 'rating' | 'documents' | 'twoRow' | 'checklist';
            columnGrouping?: string;
            hideColumn?: boolean;
            excelOptions?: ExcelColumnOptions;
        }[];
        columnGroups?: {
            id: string;
            label: string;
        }[];
    }[];
    hideAllFilters: boolean;
    searchLabel: string;
    searchTitle: string;
    filters: {
        id: string;
        label: string;
        jsonPath: string;
        matchesFrom: string;
        sortBy?: string;
        defaultValue: string;
        sortOrderId: string;
        sortOrderJsonPath: string;
    }[];
    fundUrlMapping: {
        parameters: {
            key: string;
            value: string;
        }[];
        fundUrlTag: string;
    }[];
    fundUrl: string;
    fundUrlBase: string;
    fundPdfUrlBase: string;
    fundPdfName: string;
    grouping?: {
        propertyId: string;
        jsonPath: string;
    };
    constantDataPoints: {
        key: string;
        value: string;
    }[];
    downloadFileOptions: DownloadFileOptions;
    transformFunction?: string;
}

export interface ExcelColumnOptions {
    showColumn: string;
    excelLabel: string;
    dataKey: string;
    format: string;
    columnType: string;
}

export interface SheetTabOption {
    name: string;
    showDisclaimer: boolean;
    disclaimers: [{ filterBy: string; showMarker: boolean; filters: AnnotationFilters }];
}

export interface DownloadFileOptions {
    fileType?: string;
    excelTabs: SheetTabOption[];
    csvOptions: SheetTabOption;
}

export interface FirstColumnOption {
    type: string;
    id: string;
    jsonPath: string;
    iconType: string;
    addIconName?: string;
    removeIconName?: string;
    addIconURL?: string;
    removeIconURL?: string;
    storageName: string;
}

export interface View {
    id: string;
    label: string;
    subHeading: string;
    columns?: {
        id: string;
        label: string;
        format?: string;
        nullGuard?: string;
        noDataAnnotationId?: string;
        annotationId?: string;
        additionalFormat?: string;
        disableSorting: boolean;
        columnType?: 'risk' | 'price' | 'rating' | 'documents' | 'twoRow' | 'checklist';
        columnGrouping?: string;
        hideColumn?: boolean;
        excelOptions?: ExcelColumnOptions;
    }[];
    columnGroups?: {
        id: string;
        label: string;
    }[];
}

export interface Filter {
    id: string;
    label: string;
    values: {
        label: string;
        value: string;
        sortOrder: number;
    }[];
    defaultValue: string;
    sortOrderId: string;
    sortOrderJsonPath: string;
}

export interface Grouping {
    propertyId: string;
    groups: {
        label: string;
        value: string;
    }[];
}

const preparePnPViewModel = (
    models: any[],
    { views, filters, grouping, firstColumnOptions, constantDataPoints }: Partial<Config>,
    i18n: localization.II18N
) => {
    const dataPointsToFlatten: {
        id: string;
        jsonPath: string;
        transformFunction?: (input: any) => any;
        additionalId?: string;
        additionalJsonPath?: string;
        additionalFormat?: string;
        sortOrder?: string;
    }[] = [];
    const processedViews: View[] = [];
    const processedFilters: Filter[] = [];
    const processedModels: Record<string, any>[] = [];
    let processedGrouping: Grouping | null = null;
    const firstColumnDataKey: string = firstColumnOptions?.jsonPath || 'entityId';
    const firstColumnId: string = firstColumnOptions?.id || 'entityId';

    views &&
        views.forEach((v) => {
            const columns =
                v.columns?.map((c) => {
                    const dataPointToFlatten: {
                        id: string;
                        jsonPath: string;
                        transformFunction?: (input: any) => any;
                        additionalId?: string;
                        additionalJsonPath?: string;
                    } = {
                        id: c.id,
                        jsonPath: c.jsonPath,
                    };

                    if (c.transformFunction) {
                        dataPointToFlatten.transformFunction = c.transformFunction;
                    }

                    if (c.additionalId && c.additionalJsonPath) {
                        dataPointToFlatten.additionalId = c.additionalId;
                        dataPointToFlatten.additionalJsonPath = c.additionalJsonPath;
                    }

                    dataPointsToFlatten.push(dataPointToFlatten);

                    const processedColumns: {
                        id: string;
                        label: string;
                        format?: string;
                        noDataAnnotationId?: string;
                        annotationId?: string;
                        nullGuard?: string;
                        additionalFormat?: string;
                        disableSorting: boolean;
                        columnType?:
                            | 'risk'
                            | 'price'
                            | 'rating'
                            | 'documents'
                            | 'twoRow'
                            | 'checklist';
                        columnGrouping?: string;
                        hideColumn?: boolean;
                        excelOptions?: ExcelColumnOptions;
                    } = { id: c.id, label: c.label, disableSorting: c.disableSorting };

                    if (c.format) {
                        processedColumns.format = c.format;
                    }

                    if (c.nullGuard) {
                        processedColumns.nullGuard = c.nullGuard;
                    }

                    if (c.noDataAnnotationId) {
                        processedColumns.noDataAnnotationId = c.noDataAnnotationId;
                    }

                    if (c.annotationId) {
                        processedColumns.annotationId = c.annotationId;
                    }

                    if (c.columnType) {
                        processedColumns.columnType = c.columnType;
                    }

                    if (c.columnGrouping) {
                        processedColumns.columnGrouping = c.columnGrouping;
                    }

                    if (c.additionalFormat) {
                        processedColumns.additionalFormat = c.additionalFormat;
                    }

                    if (c.hideColumn) {
                        processedColumns.hideColumn = c.hideColumn;
                    }

                    if (c.excelOptions) {
                        processedColumns.excelOptions = c.excelOptions;
                    }

                    return processedColumns;
                }) || [];

            processedViews.push({
                id: v.id,
                label: v.label,
                subHeading: v.subHeading,
                columns,
                columnGroups: v.columnGroups,
            });
        });

    filters &&
        filters.forEach((f) => {
            dataPointsToFlatten.push({ id: f.id, jsonPath: f.jsonPath });
            dataPointsToFlatten.push({
                id: f.sortOrderId || '',
                jsonPath: f.sortOrderJsonPath || '',
            });
            processedFilters.push({
                id: f.id,
                label: f.label,
                values: [
                    {
                        value: 'all',
                        label: i18n.translate('widgets_fundList_filters_all') as string,
                        sortOrder: 0,
                    },
                ],
                defaultValue: f.defaultValue || '',
                sortOrderId: f.sortOrderId || '',
                sortOrderJsonPath: f.sortOrderJsonPath || '',
            });
        });

    if (grouping) {
        dataPointsToFlatten.push({ id: grouping.propertyId, jsonPath: grouping.jsonPath });
        processedGrouping = {
            propertyId: grouping.propertyId,
            groups: [],
        };
    }

    const uniqueGroupsForGrouping: { value: string; label: string }[] = [];

    models.forEach((model) => {
        const processedModel: Record<string, any> = {};

        dataPointsToFlatten.forEach((d) => {
            const jsonPaths = d.jsonPath.split(',');

            const { value: dataPointValue }: any = JsonQuery(jsonPaths[0], { data: model });
            let dataPoint = dataPointValue;

            jsonPaths.forEach((p) => {
                if (!dataPoint) {
                    const { value: dataPointValue }: any = JsonQuery(p, { data: model });
                    dataPoint = dataPointValue;
                }
            });

            processedModel[d.id] = dataPoint || null;

            if (d.transformFunction) {
                // eslint-disable-next-line no-new-func
                processedModel[d.id] = dataPoint
                    ? commonUtils.transformWithGlobals(
                          d.transformFunction as unknown as string,
                          dataPoint
                      )
                    : null;
                processedModel[`original${d.id}`] = dataPoint || null;
            }

            if (d.additionalId && d.additionalJsonPath) {
                const { value: additionalDataPoint }: any = JsonQuery(d.additionalJsonPath, {
                    data: model,
                });

                let item1 = dataPoint || null;
                const item2 = additionalDataPoint;

                if (d.transformFunction) {
                    // eslint-disable-next-line no-new-func
                    item1 = dataPoint
                        ? commonUtils.transformWithGlobals(
                              d.transformFunction as unknown as string,
                              dataPoint
                          )
                        : null;
                }

                processedModel[d.id] = { item1, item2 };
            }
        });

        constantDataPoints &&
            constantDataPoints.forEach((c) => {
                processedModel[c.key] = c.value || null;
            });

        processedFilters.forEach((f) => {
            const filterValue: any = processedModel[f.id];

            if (typeof filterValue === 'string' && !f.values.find((v) => v.value === filterValue)) {
                f.values.push({
                    value: filterValue,
                    label: filterValue,
                    sortOrder: processedModel[f.sortOrderId || '']?.sortOrder || 1,
                });
            } else if (Array.isArray(filterValue)) {
                filterValue.forEach((fv) => {
                    if (!f.values.find((v) => v.value === fv.value)) {
                        f.values.push({
                            value: fv.value,
                            label: fv.label,
                            sortOrder: fv?.sortOrder?.sortOrder || 1,
                        });
                    }
                });
            }

            f.values = f.values.sort((a, b) => a.sortOrder - b.sortOrder);
        });

        if (processedGrouping) {
            const groupingValue = processedModel[processedGrouping.propertyId];

            if (groupingValue && !uniqueGroupsForGrouping.find((g) => g.value === groupingValue)) {
                uniqueGroupsForGrouping.push({
                    value: groupingValue,
                    label: groupingValue,
                });
            }
        }

        // adding data for first column
        const { value: firstColumnData }: any = JsonQuery(firstColumnDataKey, { data: model });
        processedModel[firstColumnId] = firstColumnData;

        processedModels.push(processedModel);
    });

    if (processedGrouping) {
        processedGrouping.groups = uniqueGroupsForGrouping;
    }

    return {
        data: processedModels,
        views: processedViews,
        filters: processedFilters,
        grouping: processedGrouping || null,
    };
};

export default preparePnPViewModel;
